import { BffClient } from "@made-people/gears-bff-client";
import {
  CentraProductsRequestV1,
  FrontendContextV1,
} from "@made-people/nestjs-bff-service";
import { crc32 } from "@made-people/functions-crc32";
import {
  centraFilterResponse,
  CentraGetProductsResponse,
  type CentraGetProductsPayload,
} from "@made-people/centra-checkout-api";
import { SearchResult } from "@made-people/grease-search";
import {
  CentraSearchFunction,
  centraFiltersToGreaseFacets,
} from "@made-people/grease-centra";
import { frontendProductToGreaseProduct } from "./mappers/frontend-product-to-grease-product";

const requestCache = new Map();
function getCache(key: string) {
  if (typeof window === "undefined") {
    return undefined;
  }
  requestCache.get(key);
}
function setCache(key: string, value: unknown) {
  if (typeof window === "undefined") {
    return;
  }
  requestCache.set(key, value);
}

export function bffCentraSearch(
  bffClient: BffClient,
  initialPayload: CentraGetProductsPayload,
  frontendContext?: FrontendContextV1,
): CentraSearchFunction {
  return async (payload: CentraGetProductsPayload) => {
    const emptyResult = {
      items: [],
      meta: {
        itemsPerPage: 0,
        totalItems: 0,
        totalPages: 0,
        currentPage: 0,
      },
      facets: [],
      sort: [],
    };
    if (!frontendContext) {
      return emptyResult;
    }

    const bffPayload: CentraProductsRequestV1 = {
      frontendContext,
      payload,
    };

    const cacheKey = crc32(JSON.stringify(bffPayload));
    let result: SearchResult | undefined = getCache(cacheKey);
    if (result) {
      return result;
    }

    const response = await bffClient.fetchFrontendProducts(bffPayload);

    if (!response) {
      return emptyResult;
    }

    const limit = payload.limit ?? 20;
    const totalItems = response.productCount;
    const skip = payload.skipFirst ?? 0;
    const currentPage = skip / limit + 1;

    const items = response.frontendProducts.map((frontendProduct) => {
      const product = frontendProductToGreaseProduct(
        frontendProduct,
        frontendContext,
        undefined,
      );
      return {
        type: "product" as const,
        product,
      };
    });

    const meta = {
      itemsPerPage: limit,
      totalItems,
      totalPages: Math.ceil(totalItems / limit),
      currentPage,
    };

    /**
     * Need to parse the filters from the response, since types are not shared between the BFF and the grease-centra package
     */
    const centraFilters: CentraGetProductsResponse["filter"] = [];

    response.filter?.forEach((filter) => {
      const parsedFilters = centraFilterResponse.safeParse(filter);
      if (parsedFilters.success) {
        centraFilters.push(parsedFilters.data);
      }
    });

    const facets =
      centraFiltersToGreaseFacets(centraFilters, payload, initialPayload) ?? [];

    result = {
      items,
      meta,
      facets,
      sort: [], // Not yet supported
    };

    setCache(cacheKey, result);
    return result;
  };
}
